/**
 * Connection State Machine
 * Manages complex connection state transitions with clear rules
 */

import type { ConnectionState } from './WebRTCProvider';

/**
 * Connection state transition events
 */
export type ConnectionEvent = 
  | 'CONNECT'
  | 'CONNECTION_ESTABLISHED' 
  | 'CONNECTION_FAILED'
  | 'DISCONNECT'
  | 'RECONNECT'
  | 'CHANNEL_CHANGE_START'
  | 'CHANNEL_CHANGE_COMPLETE'
  | 'ERROR';

/**
 * State transition rules
 */
interface StateTransition {
  from: ConnectionState;
  event: ConnectionEvent;
  to: ConnectionState;
  guard?: () => boolean;
}

/**
 * Connection State Machine class
 * Ensures valid state transitions and prevents invalid state changes
 */
export class ConnectionStateMachine {
  private currentState: ConnectionState = 'idle';
  private transitions: StateTransition[] = [
    // Basic connection flow
    { from: 'idle', event: 'CONNECT', to: 'connecting' },
    { from: 'connecting', event: 'CONNECTION_ESTABLISHED', to: 'connected' },
    { from: 'connecting', event: 'CONNECTION_FAILED', to: 'failed' },
    { from: 'connecting', event: 'ERROR', to: 'failed' },
    
    // Allow CONNECTION_ESTABLISHED from connected state (for re-establishment scenarios)
    { from: 'connected', event: 'CONNECTION_ESTABLISHED', to: 'connected' },
    
    // Disconnection
    { from: 'connected', event: 'DISCONNECT', to: 'disconnected' },
    { from: 'failed', event: 'DISCONNECT', to: 'disconnected' },
    
    // Reconnection
    { from: 'disconnected', event: 'RECONNECT', to: 'connecting' },
    { from: 'failed', event: 'RECONNECT', to: 'connecting' },
    
    // Channel changes (don't trigger state changes)
    { from: 'connected', event: 'CHANNEL_CHANGE_START', to: 'connected' },
    { from: 'connected', event: 'CHANNEL_CHANGE_COMPLETE', to: 'connected' },
    
    // Error from any state
    { from: 'connecting', event: 'ERROR', to: 'failed' },
    { from: 'connected', event: 'ERROR', to: 'failed' },
    { from: 'disconnected', event: 'ERROR', to: 'failed' },
  ];

  private callbacks: {
    onStateChange?: (newState: ConnectionState, previousState: ConnectionState, event: ConnectionEvent) => void;
    onInvalidTransition?: (currentState: ConnectionState, event: ConnectionEvent) => void;
  } = {};

  constructor(initialState: ConnectionState = 'idle') {
    this.currentState = initialState;
  }

  /**
   * Get current state
   */
  getState(): ConnectionState {
    return this.currentState;
  }

  /**
   * Check if transition is valid
   */
  canTransition(event: ConnectionEvent): boolean {
    return this.transitions.some(
      t => t.from === this.currentState && 
           t.event === event && 
           (!t.guard || t.guard())
    );
  }

  /**
   * Execute state transition
   */
  transition(event: ConnectionEvent): boolean {
    const transition = this.transitions.find(
      t => t.from === this.currentState && 
           t.event === event && 
           (!t.guard || t.guard())
    );

    if (!transition) {
      console.warn(`🚫 [ConnectionStateMachine] Invalid state transition: ${this.currentState} + ${event}`);
      this.callbacks.onInvalidTransition?.(this.currentState, event);
      return false;
    }

    const previousState = this.currentState;
    this.currentState = transition.to;
    
    this.callbacks.onStateChange?.(this.currentState, previousState, event);
    
    return true;
  }

  /**
   * Set event callbacks
   */
  setCallbacks(callbacks: {
    onStateChange?: (newState: ConnectionState, previousState: ConnectionState, event: ConnectionEvent) => void;
    onInvalidTransition?: (currentState: ConnectionState, event: ConnectionEvent) => void;
  }): void {
    this.callbacks = callbacks;
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    const previousState = this.currentState;
    this.currentState = 'idle';
    this.callbacks.onStateChange?.('idle', previousState, 'DISCONNECT');
  }
}